/* voyado-cart-recommendations.js
 * Reads window.VOYADO_CART_RECS_CONFIG set by sections/cart-drawer.liquid.
 * Cart variant IDs come from data-cart-variants attribute (refreshed via SRA on cart updates).
 * Fetches Voyado cart-page API and renders a horizontal scroll carousel.
 */

// The Elevate SDK (window.elevate) handles all request plumbing — cluster URL,
// market/locale/touchpoint, and customerKey/sessionKey (incl. the logged-in key
// via its session handler) are auto-attached to every query.* / notify.* call.
const PLUS_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

class VoyadoCartRecommendations extends HTMLElement {
  #grid = null;
  #ac   = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#init(this.#ac.signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #init(signal) {
    await window.voyadoElevateReady;
    if (signal.aborted || !window.elevate?.query) return;

    const variants = this.dataset.cartVariants?.trim();
    if (!variants) {
      this.#removeSkeleton();
      this.hidden = true;
      return;
    }
    this.#grid = this.querySelector("[data-voyado-grid]");
    if (!this.#grid) return;

    dragScroll(this.#grid, signal);

    this.#grid.addEventListener("click", (e) => {
      const link = e.target.closest("a[data-voyado-ticket]");
      if (link) this.#sendClick(link.dataset.voyadoTicket);
    }, { capture: true, signal });
    this.#fetch(variants);
  }

  async #fetch(variants) {
    try {
      // market/locale/touchpoint/customerKey/sessionKey are auto-attached by the SDK.
      const data = await window.elevate.query.cartPage(
        { cart: variants.split(",") },
        { recommendationLists: [{ id: "recs", algorithm: "CART" }] }
      );
      this.#render(data);
    } catch (e) {
      console.error("Voyado cart recommendations:", e);
      this.#removeSkeleton();
      this.hidden = true;
    }
  }

  #render(data) {
    this.#removeSkeleton();
    const groups = data.recommendationLists?.[0]?.productGroups ?? [];
    if (!groups.length) {
      this.hidden = true;
      return;
    }
    const frag = document.createDocumentFragment();
    let i = 0;
    for (const g of groups) {
      const p = g.products?.[0] ?? g;
      const ticket = g.ticket ?? p.ticket ?? "";
      const item = document.createElement("div");
      item.className = "cart-recs__card";
      item.setAttribute("role", "listitem");
      item.innerHTML = this.#card(p, ticket, i++);
      frag.appendChild(item);
    }
    this.#grid.appendChild(frag);
  }

  #removeSkeleton() {
    this.#grid
      ?.querySelectorAll(".voyado-skeleton")
      .forEach((el) => el.remove());
  }

  #card(p, ticket, i) {
    const url = this.#safeUrl(p.link);
    const title = p.title ?? "";
    const vendor = p.brand ?? "";
    const sp = p.sellingPrice?.min ?? null;
    const lp = p.listPrice?.min ?? null;
    const sale = lp !== null && sp !== null && Number(lp) > Number(sp);
    const rawIdle = p.imageInfo?.images?.[0]?.sources?.[0]?.url ?? null;
    const img = rawIdle ? this.#resized(this.#safeUrl(rawIdle.startsWith("http") ? rawIdle : `https:${rawIdle}`), 400) : null;
    const handle = new URL(this.#safeUrl(url), location.origin).pathname.split('/products/')[1]?.split('/')[0] ?? '';

    return `<div class="cart-recs__media">
        <a class="cart-recs__img-link" href="${this.#escA(url)}"${ticket ? ` data-voyado-ticket="${this.#escA(ticket)}"` : ""}>
          <div class="cart-recs__img-wrap">
            ${
              img
                ? `<img class="cart-recs__img" src="${this.#escA(img)}" alt="${this.#escA(title)}" loading="${i < 4 ? "eager" : "lazy"}" decoding="${i === 0 ? "sync" : "async"}"${i === 0 ? ' fetchpriority="high"' : ""}>`
                : '<div class="cart-recs__img--placeholder"></div>'
            }
          </div>
        </a>
        <quick-buy class="cart-recs__quick-buy" data-product-url="/products/${this.#escA(handle)}" data-has-sizes="false" data-has-colors="false" data-color-option="0" data-size-option="0" data-first-color="">
          <form action="/cart/add" method="post" class="product-card__atc-form">
            <input type="hidden" name="id" value="">
            <input type="hidden" name="quantity" value="1">
            <button class="product-card__atc cart-recs__atc" type="submit" aria-label="Legg i handlevogn" data-variant-id=""${!p.inStock ? ' disabled' : ''}>${PLUS_SVG}</button>
          </form>
        </quick-buy>
      </div>
      <div class="cart-recs__info">
        ${vendor ? `<p class="cart-recs__vendor">${this.#esc(vendor)}</p>` : ""}
        <a class="cart-recs__name" href="${this.#escA(url)}"${ticket ? ` data-voyado-ticket="${this.#escA(ticket)}"` : ""}>${this.#esc(title)}</a>
        <div class="cart-recs__price">
          ${sale ? `<span class="cart-recs__price-was">${this.#fmt(lp)}</span>` : ""}
          ${sp !== null ? `<span class="cart-recs__price-now">${this.#fmt(sp)}</span>` : ""}
        </div>
      </div>`;
  }

  // notify.click sends with keepalive (survives the link navigation) and
  // auto-attaches the keys. Stash the ticket first so voyado-atc can pair a
  // later add-to-cart with this click.
  #sendClick(ticket) {
    if (!ticket || !window.elevate?.notify) return;
    try {
      sessionStorage.setItem("voyado_pending_atc_ticket", ticket);
    } catch {}
    window.elevate.notify.click(ticket);
  }

  #fmt(v) {
    if (v == null) return "";
    const n = typeof v === "object" ? Number(v.min ?? v.amount ?? 0) : Number(v);
    return Math.round(n).toLocaleString("nb-NO") + " kr";
  }

  #esc(s) {
    return String(s).replace(
      /[&<>"]/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]
    );
  }
  #escA(s) {
    return String(s).replace(/["']/g, (c) => (c === '"' ? "&quot;" : "&#39;"));
  }
  #safeUrl(u) {
    const s = String(u ?? "");
    return s.startsWith("/") || s.startsWith("https://") || s.startsWith("http://")
      ? s
      : "#";
  }

  // Cards are 140px wide, so 400px covers 2x retina without the master file.
  #resized(url, width) {
    if (!url || url === "#") return url;
    return url.includes("?") ? `${url}&width=${width}` : `${url}?width=${width}`;
  }
}

customElements.define("voyado-cart-recommendations", VoyadoCartRecommendations);
